import React, { useState, useEffect } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import EmployesPage from './pages/EmployesPage';
import DashboardPage from './pages/DashboardPage';
import NouvelEmployePage from './pages/NouvelEmployePage';
import ParametresPage from './pages/ParametresPage';
import CentreConfigurationPage from './pages/configuration/CentreConfigurationPage';
import ConfigurationDomainePage from './pages/configuration/ConfigurationDomainePage';
import AValiderConfigurationPage from './pages/configuration/AValiderConfigurationPage';
import ReglementationPage from './pages/configuration/ReglementationPage';
import PackReglementairePage from './pages/configuration/PackReglementairePage';
import JournalConfigurationPage from './pages/configuration/JournalConfigurationPage';
import VersionsMobilePage from './pages/configuration/VersionsMobilePage';
import PointagePage from './pages/PointagePage';
import EmployeProfilPage from './pages/EmployeProfilPage';
import Layout from './components/Layout';
import GestionTempsPage from './pages/GestionTempsPage';
import RecapMensuelPage from './pages/RecapMensuelPage';
import RecapAnnuelPage from './pages/RecapAnnuelPage';
import EmployeHomePage from './pages/EmployeHomePage';
import EmployeEvolutionPage from './pages/EmployeEvolutionPage';
import CongesPage from './pages/CongesPage';
// Moteur Congés (Lot 3) — droits à congé, vue DRH : droits contrôlés par le serveur (/api/droits-conges).
import DroitsCongesPage from './pages/conges/DroitsCongesPage';
import FicheDroitsCongesPage from './pages/conges/FicheDroitsCongesPage';
import PermissionsEmployePage from './pages/PermissionsEmployePage';
import MetiersPage from './pages/MetiersPage';
import ProvisionRetraitePage from './pages/ProvisionRetraitePage';
import ChangerMotDePassePage from './pages/ChangerMotDePassePage';
import MesPrêtsPage from './pages/MesPrêtsPage';
import DettesPage from './pages/DettesPage';
import IpmPage from './pages/IpmPage';
import IpmComptePage from './pages/IpmComptePage';
import IpmEquipePage from './pages/IpmEquipePage';
// ImportEmployesPage déplacé dans ParametresPage → section import-export
import PostesPage from './pages/PostesPage';
import AuditPage from './pages/AuditPage';
import PretsSogasPage from './pages/PretsSogasPage';
import GouvernancePage from './pages/GouvernancePage';
import DossierSalarialQualitePage from './pages/DossierSalarialQualitePage';
import PreparationPaiePage from './pages/PreparationPaiePage';
// Chantier temps — Temps › Réglages (agent D) ; la navigation sera intégrée par l'agent DRH.
import ReglagesHorairesPage from './pages/temps/ReglagesHorairesPage';
import ReglagesFeriesPage from './pages/temps/ReglagesFeriesPage';
// Chantier temps — Mes demandes et Arrêts et certificats (agent E2).
import MesDemandesPage from './pages/temps/MesDemandesPage';
import ArretsCertificatsPage from './pages/temps/ArretsCertificatsPage';
import BornePage from './pages/temps/BornePage';
// Chantier temps — écrans du responsable (agent H) : droits vérifiés par le serveur (/api/temps/equipe/droits).
import EquipeAujourdhuiPage from './pages/temps/EquipeAujourdhuiPage';
import ATraiterPage from './pages/temps/ATraiterPage';
import CalendrierEquipePage from './pages/temps/CalendrierEquipePage';
// Chantier temps — écrans de la DRH et de la RH (agent I) et menu « Temps » : droits vérifiés par le serveur (/api/temps/rh/droits).
import RhTempsTableauDeBordPage from './pages/temps/RhTempsTableauDeBordPage';
import RhATraiterPage from './pages/temps/RhATraiterPage';
import RhJourneesPage from './pages/temps/RhJourneesPage';
import RhCloturePage from './pages/temps/RhCloturePage';
import RhRevuePage from './pages/temps/RhRevuePage';
import RhQualitePage from './pages/temps/RhQualitePage';
import ReglagesBornesPage from './pages/temps/ReglagesBornesPage';
import { TempsAccueil } from './pages/temps/rhCommun';
import { pageAccueil, ROLES_TABLEAU_DE_BORD, estExterne } from './utils/pageAccueil';
// Chantier dispositifs sociaux (équipe WEB) : routes déclarées dans pages/dispositifs/routes.jsx.
import { routesDispositifs } from './pages/dispositifs/routes';
// Suivi « prochain acteur » : vue « Dossiers en attente » (droit vérifié par le serveur, GET /api/workflow/en-attente).
import DossiersEnAttentePage from './pages/workflow/DossiersEnAttentePage';

// ─── Chargement initial ────────────────────────────────────────────
const LoadingScreen = () => (
  <div style={{
    minHeight: '100vh', background: '#0a1f0a',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff',
    fontSize: 16, fontFamily: 'sans-serif'
  }}>
    Chargement SOGAS...
  </div>
);

// ─── Message accès refusé ──────────────────────────────────────────
const AccesRefuse = () => {
  const { employe } = useAuth();
  const navigate = useNavigate();
  // Retour vers une page ouverte au rôle (jamais vers le refus d'où l'on vient).
  const accueil = pageAccueil(employe);
  const libelleRetour = accueil === '/accueil-employe' ? 'Voir ma carte'
    : accueil === '/dashboard' ? 'Retour au tableau de bord'
      : accueil === '/ipm' ? 'Retour à l\'IPM' : 'Retour à ma page d\'accueil';

  const handleRetour = () => navigate(accueil);

  return (
    <Layout>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', fontFamily: 'Segoe UI, sans-serif', textAlign: 'center', gap: 16,
      }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FFEBEE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#C62828" strokeWidth="2"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="#C62828" strokeWidth="2" strokeLinecap="round"/></svg>
        </div>
        <h2 style={{ color: '#C62828', margin: 0 }}>Accès non autorisé</h2>
        <p style={{ color: '#666', fontSize: 14, maxWidth: 400 }}>
          Vous n'avez pas les droits nécessaires pour accéder à cette page.
        </p>
        <button onClick={handleRetour} style={{ background: '#1B5E20', color: '#fff', padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          {libelleRetour}
        </button>
      </div>
    </Layout>
  );
};

// ─── Rôles avec accès total (admin bypass toutes les restrictions) ─
const ROLES_DIRECTION = ['admin', 'directeur_general', 'directeur_adjoint'];

// ─── Prestataire externe de l'IPM : seules l'IPM et le changement de mot de passe ───
// (le serveur refuse de toute façon tout autre module : liste blanche côté API).
const cheminOuvertExterne = (chemin) => chemin === '/ipm' || chemin.startsWith('/ipm/')
  || chemin === '/changer-mot-de-passe' || chemin === '/login';

// ─── Route protégée (authentification + rôle optionnel) ───────────
const ProtectedRoute = ({ children, rolesAutorises, permissionAlternative }) => {
  const { employe, loading, token } = useAuth();
  const location = useLocation();
  const [dbCheck, setDbCheck] = useState(null); // null=en cours, true/false=résultat

  useEffect(() => {
    if (!permissionAlternative || !employe || !token) { setDbCheck(null); return; }
    // Admin et direction bypassent — pas d'appel DB nécessaire
    if (employe.role === 'admin' || ROLES_DIRECTION.includes(employe.role)) { setDbCheck(true); return; }
    // Rôle directement autorisé — pas d'appel DB nécessaire
    if (rolesAutorises && rolesAutorises.includes(employe.role)) { setDbCheck(true); return; }
    // Vérification temps réel en DB pour les permissions de poste
    axios.get(`${API_URL}/api/auth/verifier-acces`, {
      params: { permission: permissionAlternative },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setDbCheck(r.data.autorise === true))
      .catch(() => setDbCheck(false));
  }, [permissionAlternative, employe?.id, token]);

  if (loading) return <LoadingScreen />;
  if (!employe) return <Navigate to="/login" replace />;

  // Prestataire externe : mot de passe provisoire à changer d'abord, puis l'IPM uniquement.
  if (estExterne(employe)) {
    if (employe.premier_connexion && location.pathname !== '/changer-mot-de-passe') {
      return <Navigate to="/changer-mot-de-passe" replace state={{ premierConnexion: true }} />;
    }
    if (!cheminOuvertExterne(location.pathname)) return <Navigate to="/ipm" replace />;
    return children;
  }

  // admin et direction ont accès à tout
  if (employe.role === 'admin' || ROLES_DIRECTION.includes(employe.role)) return children;

  // Accès via permission de poste : attendre la vérification DB
  if (permissionAlternative) {
    if (dbCheck === null) return <LoadingScreen />;
    if (dbCheck) return children;
    if (rolesAutorises && rolesAutorises.includes(employe.role)) return children;
    return <AccesRefuse />;
  }

  if (rolesAutorises && !rolesAutorises.includes(employe.role)) return <AccesRefuse />;
  return children;
};

// ─── Redirection intelligente après login ─────────────────────────
const RootRedirect = () => {
  const { employe, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!employe) return <Navigate to="/login" replace />;

  return <Navigate to={pageAccueil(employe)} replace />;
};

// Module 1 (D-E2) : une seule fiche salarié, à l'adresse /employes/:id. Les anciens écrans
// (ancienne fiche, page « Modifier », rapport imprimable) redirigent vers la section concernée.
function VersFicheSalarie({ onglet }) {
  const { id } = useParams();
  return <Navigate to={`/employes/${id}${onglet ? `?onglet=${onglet}` : ''}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Login */}
          <Route path="/login" element={<LoginPage />} />

          {/* Borne de pointage de site (chantier temps) : page kiosque, authentifiée par sa clé d'appareil, sans compte */}
          <Route path="/borne" element={<BornePage />} />

          {/* Racine → redirection selon rôle */}
          <Route path="/" element={<RootRedirect />} />

          {/* Dashboard — rôles de ROLES_TABLEAU_DE_BORD (utils/pageAccueil), plus admin et direction */}
          <Route path="/dashboard" element={
            <ProtectedRoute rolesAutorises={ROLES_TABLEAU_DE_BORD}>
              <DashboardPage />
            </ProtectedRoute>
          } />

          {/* Employés — pas pour les simples employés (sauf permission voir_subordonnes) */}
          <Route path="/employes" element={
            <ProtectedRoute
              rolesAutorises={[
                'drh', 'rh_principal', 'assistant_rh',
                'prepose_regional', 'responsable_site',
                'chef_departement', 'chef_service', 'chef_equipe'
              ]}
              permissionAlternative="voir_subordonnes"
            >
              <EmployesPage />
            </ProtectedRoute>
          } />
          <Route path="/employes/nouveau" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']}>
              <NouvelEmployePage />
            </ProtectedRoute>
          } />
          {/* Module 1 : référentiel des métiers (RH). */}
          <Route path="/employes/metiers" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']}>
              <MetiersPage />
            </ProtectedRoute>
          } />
          {/* Fiche salarié unique. /profil reste reconnu (liens existants, espace salarié). */}
          <Route path="/employes/:id" element={
            <ProtectedRoute>
              <EmployeProfilPage />
            </ProtectedRoute>
          } />
          <Route path="/employes/:id/profil" element={
            <ProtectedRoute>
              <EmployeProfilPage />
            </ProtectedRoute>
          } />
          <Route path="/employes/:id/modifier" element={<VersFicheSalarie onglet="personnel" />} />

          {/* Accueil employé — aussi pour journalier (travailleur journalier) */}
          <Route path="/accueil-employe" element={
            <ProtectedRoute rolesAutorises={['employe', 'journalier']}>
              <EmployeHomePage />
            </ProtectedRoute>
          } />

          {/* Pointage — tous les rôles */}
          <Route path="/pointage" element={
            <ProtectedRoute>
              <PointagePage />
            </ProtectedRoute>
          } />

          {/* Paramètres — Centre de configuration (Lot 1) : une seule porte. Les droits par domaine sont
              contrôlés par le serveur (IAM) ; l'écran n'affiche que ce que le serveur autorise. */}
          <Route path="/parametres" element={<ProtectedRoute><CentreConfigurationPage /></ProtectedRoute>} />
          <Route path="/parametres/a-valider" element={<ProtectedRoute><AValiderConfigurationPage /></ProtectedRoute>} />
          <Route path="/parametres/reglementation" element={<ProtectedRoute><ReglementationPage /></ProtectedRoute>} />
          <Route path="/parametres/reglementation/:id" element={<ProtectedRoute><PackReglementairePage /></ProtectedRoute>} />
          <Route path="/parametres/journal" element={<ProtectedRoute><JournalConfigurationPage /></ProtectedRoute>} />
          {/* Application mobile SOGAS Employé : versions et mises à jour (0077) — droit gerer_versions_mobile, contrôlé par le serveur */}
          <Route path="/parametres/application-mobile" element={<ProtectedRoute><VersionsMobilePage /></ProtectedRoute>} />
          {/* Référentiels et réglages existants (rattachement au Centre dans les lots suivants) — drh et rh_principal */}
          <Route path="/parametres/referentiels" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal']}>
              <ParametresPage />
            </ProtectedRoute>
          } />
          <Route path="/parametres/postes" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal']}>
              <PostesPage />
            </ProtectedRoute>
          } />
          <Route path="/parametres/:domaine" element={<ProtectedRoute><ConfigurationDomainePage /></ProtectedRoute>} />

          {/* ── Module Temps (chantier temps, agent I) : /temps ouvre la première rubrique autorisée par le serveur ── */}
          <Route path="/temps" element={<ProtectedRoute><TempsAccueil /></ProtectedRoute>} />
          <Route path="/temps/rh" element={<ProtectedRoute><RhTempsTableauDeBordPage /></ProtectedRoute>} />
          <Route path="/temps/rh/a-traiter" element={<ProtectedRoute><RhATraiterPage /></ProtectedRoute>} />
          <Route path="/temps/rh/journees" element={<ProtectedRoute><RhJourneesPage /></ProtectedRoute>} />
          <Route path="/temps/rh/cloture" element={<ProtectedRoute><RhCloturePage /></ProtectedRoute>} />
          <Route path="/temps/rh/revue" element={<ProtectedRoute><RhRevuePage /></ProtectedRoute>} />
          <Route path="/temps/rh/qualite" element={<ProtectedRoute><RhQualitePage /></ProtectedRoute>} />
          {/* Ancien écran « Gestion du temps » : Historique (essais), en lecture seule, réservé au service
              RH (le serveur refuse les autres comptes, revue de sécurité L-06). */}
          <Route path="/temps/historique" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']}>
              <GestionTempsPage lectureSeule />
            </ProtectedRoute>
          } />
          <Route path="/temps/mensuel" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']}>
              <RecapMensuelPage />
            </ProtectedRoute>
          } />
          <Route path="/temps/annuel" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']}>
              <RecapAnnuelPage />
            </ProtectedRoute>
          } />
          {/* Anciens paramètres du temps : remplacés par Temps › Réglages (écritures en 410, L-01 / M5). */}
          <Route path="/temps/parametres" element={<Navigate to="/parametres/temps" replace />} />

          {/* Temps › Réglages : droits vérifiés par le serveur (/api/temps/referentiels/droits). */}
          <Route path="/temps/reglages" element={<Navigate to="/temps/reglages/horaires" replace />} />
          <Route path="/temps/reglages/horaires" element={<ProtectedRoute><ReglagesHorairesPage /></ProtectedRoute>} />
          <Route path="/temps/reglages/feries" element={<ProtectedRoute><ReglagesFeriesPage /></ProtectedRoute>} />
          {/* Centre de configuration (Lot 1) : les paramètres du temps se règlent dans Paramètres › Temps et pointage. */}
          <Route path="/temps/reglages/parametres" element={<Navigate to="/parametres/temps" replace />} />
          <Route path="/temps/reglages/bornes" element={<ProtectedRoute><ReglagesBornesPage /></ProtectedRoute>} />
          {/* Temps › Mes demandes (salarié) et Arrêts et certificats (RH habilitée) : droits vérifiés par le serveur. */}
          <Route path="/temps/mes-demandes" element={<ProtectedRoute><MesDemandesPage /></ProtectedRoute>} />
          <Route path="/temps/arrets" element={<ProtectedRoute><ArretsCertificatsPage /></ProtectedRoute>} />
          {/* Écrans du responsable (agent H) : population et droits contrôlés par le serveur (403 sinon). */}
          <Route path="/temps/equipe" element={<ProtectedRoute><EquipeAujourdhuiPage /></ProtectedRoute>} />
          <Route path="/temps/equipe/calendrier" element={<ProtectedRoute><CalendrierEquipePage /></ProtectedRoute>} />
          <Route path="/temps/a-traiter" element={<ProtectedRoute><ATraiterPage /></ProtectedRoute>} />

          {/* Gestion des congés — tous les rôles connectés */}
          <Route path="/conges" element={
            <ProtectedRoute>
              <CongesPage />
            </ProtectedRoute>
          } />
          {/* Droits à congé (Lot 3) : liste et fiche DRH ; le serveur refuse (403) sans droit de consultation. */}
          <Route path="/conges/droits" element={<ProtectedRoute><DroitsCongesPage /></ProtectedRoute>} />
          <Route path="/conges/droits/:employeId" element={<ProtectedRoute><FicheDroitsCongesPage /></ProtectedRoute>} />

          {/* Évolution employé — tous les rôles */}
          <Route path="/employes/:id/evolution" element={
            <ProtectedRoute>
              <EmployeEvolutionPage />
            </ProtectedRoute>
          } />

          {/* Ancien rapport imprimable : remplacé par « Exporter le dossier (PDF) » de la fiche. */}
          <Route path="/employes/:id/rapport" element={<VersFicheSalarie />} />

          {/* Droits & Accès — RH, direction, managers */}
          <Route path="/employes/:id/permissions" element={
            <ProtectedRoute rolesAutorises={[
              'drh', 'rh_principal', 'assistant_rh',
              'prepose_regional', 'responsable_site',
              'chef_departement', 'chef_service', 'chef_equipe'
            ]}>
              <PermissionsEmployePage />
            </ProtectedRoute>
          } />

          {/* Provision Retraite */}
          <Route path="/provisions" element={
            <ProtectedRoute rolesAutorises={['rh_principal', 'drh', 'directeur_general', 'directeur_adjoint', 'assistant_rh', 'admin']}>
              <ProvisionRetraitePage />
            </ProtectedRoute>
          } />

          {/* Changer mot de passe */}
          <Route path="/changer-mot-de-passe" element={<ProtectedRoute><ChangerMotDePassePage /></ProtectedRoute>} />

          {/* RH-PAIE-1 : Centre de qualité des dossiers salariaux — la donnée réelle reste
              filtrée côté backend (admin exclu du bypass, voir dossierSalarialRoutes.js) */}
          <Route path="/dossiers-salariaux/qualite" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']} permissionAlternative="peut_voir_qualite_dossiers_salariaux">
              <DossierSalarialQualitePage />
            </ProtectedRoute>
          } />

          {/* RH-PAIE-3 : préparation mensuelle de la paie — aucun calcul de paie ni bulletin. */}
          <Route path="/paie/preparation" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal', 'assistant_rh']} permissionAlternative="peut_voir_preparation_paie">
              <PreparationPaiePage />
            </ProtectedRoute>
          } />

          {/* Prêts et retenues — vue RH + comptable */}
          <Route path="/dettes" element={
            <ProtectedRoute rolesAutorises={['rh_principal', 'assistant_rh', 'drh', 'directeur_general', 'directeur_adjoint', 'comptable', 'agent_oeuvre_sociale', 'agent_ipm']}>
              <DettesPage />
            </ProtectedRoute>
          } />

          {/* Module IPM — socle du 3 octobre 2026 : aucun accès par le rôle (ni administrateur, ni
              direction). La page n'ouvre que les onglets des habilitations IPM attribuées
              explicitement ; le serveur contrôle chaque appel. */}
          <Route path="/ipm" element={
            <ProtectedRoute>
              <IpmPage />
            </ProtectedRoute>
          } />
          {/* Équipe IPM : Agents et Gestionnaires (salariés ou prestataires externes). Pas de liste
              de rôles : le serveur contrôle (administration des accès ou supervision IPM). */}
          <Route path="/ipm/equipe" element={
            <ProtectedRoute>
              <IpmEquipePage />
            </ProtectedRoute>
          } />
          {/* Lot UX-1 : compte IPM d'un salarié (vue financière, habilitation IPM contrôlée par le serveur) */}
          <Route path="/ipm/comptes/:employeId" element={
            <ProtectedRoute>
              <IpmComptePage />
            </ProtectedRoute>
          } />

          {/* Mes prêts et retenues — employé et RH */}
          <Route path="/mes-prets" element={
            <ProtectedRoute rolesAutorises={['employe', 'rh_principal', 'drh', 'directeur_general']}>
              <MesPrêtsPage />
            </ProtectedRoute>
          } />

          {/* Import Excel Employés → déplacé dans /parametres */}
          <Route path="/import-employes" element={<Navigate to="/parametres" replace />} />

          {/* Prêts SOGAS — tous les rôles connectés */}
          <Route path="/prets-sogas" element={
            <ProtectedRoute>
              <PretsSogasPage />
            </ProtectedRoute>
          } />

          {/* Centre de Gouvernance RH — drh et rh_principal */}
          <Route path="/gouvernance" element={
            <ProtectedRoute rolesAutorises={['drh', 'rh_principal']}>
              <GouvernancePage />
            </ProtectedRoute>
          } />

          {/* Journal d'activité — rh_principal et drh uniquement */}
          <Route path="/audit" element={
            <ProtectedRoute rolesAutorises={['rh_principal', 'drh']}>
              <AuditPage />
            </ProtectedRoute>
          } />

          {/* Dossiers en attente (prochain acteur) : droit voir_dossiers_en_attente ou rôle RH global, contrôlé par le serveur (403 affiché). */}
          <Route path="/dossiers-en-attente" element={<ProtectedRoute><DossiersEnAttentePage /></ProtectedRoute>} />

          {/* Dispositifs sociaux : Œuvres sociales, instances et mandats, dispositifs (droits vérifiés par le serveur) */}
          {routesDispositifs()}

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
